import {
  selectPurchaseMethod,
  setCheckoutStatus,
  setSelectedPaymentType,
  useAppDispatch,
  useAppRouter,
  useAppropriatePaymentMethod,
  useCheckoutState,
  useCreateBankingSessionMutation,
  usePBIRejected,
} from '../../state'
import { CheckoutStatus, money } from '../../model'
import { Money, PaymentType } from '@afterpay/types'
import { Route } from '../../config/router'
import { useEffect } from 'react'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { MutationTrigger } from '@reduxjs/toolkit/dist/query/react/buildHooks'

interface PCLDeclineInterface {
  handleDecline: () => void
}

interface iMoneyHook {
  convertToMoney: (amount?: string) => Money | undefined
  symbol: string
}

export const useMoneyHooks = (): iMoneyHook => {
  const state = useCheckoutState()
  const localCurrency = state?.started?.consumerAmount?.currency ?? state?.checkout?.amount?.currency
  const symbol = state?.started?.consumerAmount?.symbol ?? state?.checkout?.amount?.symbol ?? '$'
  const convertToMoney = (amount?: string): Money | undefined => {
    return typeof amount !== 'undefined' ? money(amount, localCurrency) : undefined
  }

  return { convertToMoney, symbol }
}

export const usePCLDecline = (): PCLDeclineInterface => {
  const hasPBIRejected = usePBIRejected()
  const dispatch = useAppDispatch()
  const { push } = useAppRouter()
  const paymentMethod = useAppropriatePaymentMethod()

  const handleDecline = (): void => {
    if (!hasPBIRejected) {
      dispatch(setSelectedPaymentType(PaymentType.PBI))
      if (paymentMethod !== null) {
        dispatch(selectPurchaseMethod(paymentMethod))
      } else {
        dispatch(setCheckoutStatus(CheckoutStatus.LackOfEligiblePaymentMethod))
        push(Route.ADD_PAYMENT_METHOD)
        return
      }
      push(Route.SUMMARY)
    } else {
      push(Route.TERMINAL_ERROR)
    }
  }

  return { handleDecline }
}

interface CreateBankingSessionInterface {
  createBankSession: MutationTrigger<any>
}

export const useCreateBankingSession = (): CreateBankingSessionInterface => {
  const [createBankSession, { data: bankSessionData, reset }] = useCreateBankingSessionMutation({ fixedCacheKey: 'create-banking-session-cache' })
  const { push } = useAppRouter()
  const { logEvent } = useMonitoringWithEnduringEventProperties()

  useEffect(() => {
    const { redirectUrl, hasAccounts, hasFinances } = bankSessionData ?? {}

    const skipBasiq = hasAccounts || hasFinances
    if (redirectUrl) {
      if (skipBasiq) {
        push(Route.AFFORDABILITY_VERIFYING)
      } else {
        window.removeEventListener('beforeunload', (event) => {
          logEvent(TrackingEvent.PRESSED_CLOSE_BROWSER_TAB)
          event.preventDefault()
          event.stopPropagation()
          delete event.returnValue
        })
        window.open(redirectUrl, '_self')
      }
    }

    return () => reset()
  }, [bankSessionData])

  return { createBankSession }
}
